import React, { Component, ComponentState } from 'react';
import { StandardSubscriptionComponentArgs } from '../../helpers/StandardContainerComponent';
import ComponentName from '../../constant/ComponentName';
import CollectionNames from '../../constant/CollectionNames';
import CraListViewerComponent from './CraListViewerComponent';
import LanguageUtil from 'util/LanguageUtil';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { RootState } from 'redux/store';
import { setSelectedItems } from '../../redux/actions/ComponentActions';
import { changePageFilter, clearPage } from '../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ServiceContainer from '../../config/ServiceContainer';
import CraService from '../../service/CraService';
import { ServiceNames } from '../../constant/ServiceNames';
import { CraSegmentType, CraVariableSchema } from '../../domain/cra/CraDomains';
import { query } from '@trlogic/client-js';
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;
import FormicaSearchSelectBox from '../../components/custom/FormicaSearchSelectBox';
import FormicaInput from '../../components/custom/FormicaInput';
import AnimatedButton from '../../components/custom/AnimatedButton';
import { CraPolicyEditorModal } from './editor/CraPolicyEditorModal';

class CraListComponent extends Component<Props, State, CraVariableSchema> {
  public state: State = new State();

  protected readonly args: StandardSubscriptionComponentArgs;

  private readonly craService: CraService = ServiceContainer.getInstance().get(ServiceNames.CRA);

  constructor(props: Props) {
    const args: StandardSubscriptionComponentArgs = {
      component: ComponentName.POLICY,
      withSchema: false,
      itemCollection: CollectionNames.CRA,
      label: LanguageUtil.get({ id: 'common.label.segment', defaultMessage: 'Segment' }),
    };
    super(props, args);
    this.args = args;
  }

  protected getListView(): React.ReactNode {
    const { policy } = this.props;
    const variables = policy.variables || [];

    return <CraListViewerComponent views={true} variables={variables} openEvent={this.openEvent} currentModal={false} />;
  }

  protected openEvent = (id: string) => {
    if (id) {
      const { policy } = this.props;
      this.setState({
        editorVisible: true,
        selectedVariable: policy.variables.find((variable) => variable.name === id) || undefined,
      });
    }
  };

  private onChangeDataType = (item: { value: CraSegmentType; label: string }) => {
    this.setState({ selectedType: item.value });
  };

  private getDataTypeOptions = () => {
    return Object.entries(CraSegmentType).map(([key, value]) => ({
      label: value,
      value: value,
    }));
  };

  private addVariable = async () => {
    const { name, selectedType, weight, description } = this.state;
    const { policy } = this.props;
    if (!name || !selectedType || !weight) return;

    const variableObj: CraVariableSchema = {
      name,
      type: selectedType,
      weight: Number(weight),
      description,
      isEnabled: false,
    };

    const result = await this.craService.variable(variableObj, policy.assessor, false);
    if (result) {
      this.setState({ name: '', selectedType: CraSegmentType.STRING, weight: '', description: '' });
    }
  };

  public render() {
    const { name, weight, description, selectedType, editorVisible } = this.state;
    const { policy } = this.props;

    return (
      <div className='w-full h-full flex flex-col gap-y-3 overflow-y-hidden'>
        <div className='flex flex-col pt-5 md:flex-row flex-wrap gap-5 px-5 items-stretch md:items-center w-full'>
          <div className='w-full md:flex-[1.76] md:min-w-[150px] h-[36px]'>
            <FormicaInput
              value={name}
              onChange={(e) => this.setState({ name: e.currentTarget.value })}
              placeholder={LanguageUtil.get({ id: 'common.label.name', defaultMessage: 'Name' })}
            />
          </div>
          <div className='w-full md:flex-[1.46] md:min-w-[130px] h-[36px]'>
            <FormicaSearchSelectBox value={selectedType} rounded placeholder='Type' onChange={this.onChangeDataType} options={this.getDataTypeOptions()} />
          </div>
          <div className='w-full md:flex-[1.46] md:min-w-[130px] h-[36px]'>
            <FormicaInput
              value={weight}
              type='number'
              onChange={(e) => this.setState({ weight: e.currentTarget.value })}
              placeholder={LanguageUtil.get({ id: 'common.label.weight', defaultMessage: 'Weight' })}
            />
          </div>
          <div className='w-full md:flex-[4.44] md:min-w-[250px] h-[36px]'>
            <FormicaInput
              value={description}
              onChange={(e) => this.setState({ description: e.currentTarget.value })}
              placeholder={LanguageUtil.get({ id: 'common.label.description', defaultMessage: 'Description' })}
            />
          </div>
          <AnimatedButton
            onClick={this.addVariable}
            customSize={'sm'}
            disabled={!name || !selectedType || !weight ? true : false}
            className='w-full md:flex-[0.87] md:min-w-[100px] h-[34px]'
            icon={'add'}
            text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
            customType={'primary'}
            data-testid={'access_creator_add_button'}
          />
        </div>
        <div className='flex h-full w-full px-5'>
          <CraPolicyEditorModal
            visible={editorVisible}
            assessor={policy.assessor}
            variable={this.state.selectedVariable}
            onClose={() => this.setState({ editorVisible: false })}
          />
          {this.getListView()}
        </div>
      </div>
    );
  }
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  pagePath: state.page.pagePath,
  currentCategory: state.page.currentCategory,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
  policy: state.policy.policy,
});
const mapDispatchToProps = { clearPage, setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  filter: Filter = empty();
  name: string = '';
  weight: string | number = '';
  description: string = '';
  selectedType: CraSegmentType = CraSegmentType.STRING;
  editorVisible: boolean = false;
  selectedVariable: CraVariableSchema | undefined = undefined;
}

export default withFormica(connector(CraListComponent));
